import {
  faQuestionCircle,
  faUserFriends,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { ReadingCircleApi } from '../../API/ReadingCircleApi';
import { Avatar } from '../../Components/Avatar';
import Button from '../../Components/Button';
import { AnalyticsEvent, useAnalytics } from '../../Context/AnalyticsProvider';
import Modal from '../../Modal/Modal';
import { User } from '../../Types/User';

interface CoAuthorSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCoAuthor: (coAuthor: User | null) => void;
  initialSelectedCoAuthor?: User | null;
  onShowIntro?: () => void;
}

export const CoAuthorSelectionModal: React.FC<CoAuthorSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectCoAuthor,
  initialSelectedCoAuthor = null,
  onShowIntro,
}) => {
  const [peers, setPeers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCoAuthor, setSelectedCoAuthor] = useState<User | null>(null);
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (isOpen) {
      loadPeers();
      setSelectedCoAuthor(initialSelectedCoAuthor);
    }
  }, [isOpen, initialSelectedCoAuthor]);

  const loadPeers = async () => {
    setLoading(true);
    try {
      const readingCircleApi = new ReadingCircleApi();
      const peers = await readingCircleApi.getReadingCirclePeers();
      setPeers(peers);
    } catch (error) {
      console.error('Error loading peers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPeers = searchQuery
    ? peers.filter(
        (peer) =>
          peer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          peer.surname?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : peers;

  const handleCoAuthorSelect = (peer: User) => {
    if (selectedCoAuthor?.id === peer.id) {
      setSelectedCoAuthor(null);
    } else {
      setSelectedCoAuthor(peer);
    }
  };

  const confirmSelection = () => {
    trackEvent(
      AnalyticsEvent.BOOK_BUILDER_COAUTHOR_SELECTED,
      selectedCoAuthor
        ? {
            coAuthorId: selectedCoAuthor.id,
            coAuthorName: selectedCoAuthor.name,
          }
        : { removed: true },
    );
    onSelectCoAuthor(selectedCoAuthor);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidthClass="max-w-md"
      showCloseButton={false}
    >
      <div className="rounded-lg overflow-hidden bg-white">
        {/* Header */}
        <div className="relative p-5 border-b border-gray-200">
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={faUserFriends}
              className="text-blue-500 mr-3 text-2xl"
            />
            <div className="flex flex-col text-left">
              <h2 className="text-xl font-semibold">
                Create a Book with a Friend
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                Take turns writing chapters until the book is complete.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute right-5 top-5 text-gray-500 hover:text-gray-700"
          >
            <RxCross2 size={20} />
          </button>
        </div>

        <div className="p-5">
          {/* Friend selection section */}
          <div className="mb-4">
            <h3 className="text-left text-gray-700 font-medium mb-2">
              Choose a friend to write with:
            </h3>
            <div className="relative">
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg py-2 px-4 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Search friends..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Friend list */}
          <div className="mb-4">
            {loading ? (
              <div className="text-left py-4">Loading friends...</div>
            ) : filteredPeers.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {filteredPeers.map((peer) => {
                  return (
                    <div
                      key={peer.id}
                      className={`flex items-center justify-start p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedCoAuthor?.id === peer.id
                          ? 'bg-blue-100'
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => handleCoAuthorSelect(peer)}
                    >
                      <Avatar user={peer} size={40} />
                      <div className="ml-3">
                        <div className="font-medium">{peer.name}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-left py-4 text-gray-500">
                {searchQuery
                  ? 'No friends match your search'
                  : 'No friends found. Join a reading circle to collaborate!'}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center">
            {onShowIntro && (
              <button
                onClick={onShowIntro}
                className="inline-flex items-center text-blue-600 hover:text-blue-700"
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="mr-2" />
                <span>How does it work?</span>
              </button>
            )}
            <Button
              onClick={confirmSelection}
              disabled={!selectedCoAuthor}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700"
            >
              Select Friend
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CoAuthorSelectionModal;
